import { UserRoundIcon } from 'lucide-react';
import type { CompanyMemberDTO, CompanyMembersQueryDTO } from '@shared/schemas/organization';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import type { MembersTableColumn } from '@/components/common/members-management';
import { formatCompactNumber } from '@/features/usage/lib/format';
import { CompanyMemberSortHeader } from './CompanyMemberSortHeader';

export type CompanyMemberColumnKey = 'consumed' | 'createdAt' | 'emailVerified' | 'member' | 'role' | 'status';
export type CompanyMemberSortBy = CompanyMembersQueryDTO['sortBy'];
export type CompanyMemberSortDirection = CompanyMembersQueryDTO['sortDirection'];

export const allCompanyMemberColumns: readonly CompanyMemberColumnKey[] = [
  'member',
  'role',
  'status',
  'emailVerified',
  'consumed',
  'createdAt',
];

export const companyMemberColumnLabels: Record<CompanyMemberColumnKey, string> = {
  consumed: 'Consommation',
  createdAt: 'Ajouté le',
  emailVerified: 'Vérification',
  member: 'Membre',
  role: 'Rôle',
  status: 'Statut',
};

export function roleLabel(role: CompanyMemberDTO['role']) {
  if (role === 'owner') return 'Propriétaire';
  if (role === 'admin') return 'Administrateur';
  return 'Membre';
}

function formatDateTime(value: string) {
  return new Intl.DateTimeFormat('fr-FR', {
    dateStyle: 'medium',
  }).format(new Date(value));
}

function initials(name: string) {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');
}

export function buildCompanyMemberColumns({ direction, onSort, sortBy, visible }: {
  direction: CompanyMemberSortDirection;
  onSort: (sortBy: CompanyMemberSortBy) => void;
  sortBy: CompanyMemberSortBy;
  visible: ReadonlySet<CompanyMemberColumnKey>;
}): MembersTableColumn<CompanyMemberDTO>[] {
  const header = (
    key: CompanyMemberColumnKey,
    field: CompanyMemberSortBy = key as CompanyMemberSortBy,
  ) => (
    <CompanyMemberSortHeader
      active={sortBy === field}
      direction={direction}
      label={companyMemberColumnLabels[key]}
      onClick={() => onSort(field)}
    />
  );
  const columns: Record<CompanyMemberColumnKey, MembersTableColumn<CompanyMemberDTO>> = {
    member: {
      ariaSort: sortBy === 'name' ? direction === 'asc' ? 'ascending' : 'descending' : 'none',
      key: 'member',
      header: header('member', 'name'),
      renderCell: (member) => (
        <span className="flex min-w-[15rem] items-center gap-3">
          <Avatar className="size-9">
            <AvatarFallback>{initials(member.name) || <UserRoundIcon />}</AvatarFallback>
          </Avatar>
          <span className="min-w-0">
            <span className="block font-medium">{member.name}</span>
            <span className="block max-w-[20rem] truncate text-xs text-muted-foreground">
              {member.email}
            </span>
          </span>
        </span>
      ),
    },
    role: {
      ariaSort: sortBy === 'role' ? direction === 'asc' ? 'ascending' : 'descending' : 'none',
      key: 'role',
      header: header('role'),
      renderCell: (member) => <Badge variant="secondary">{roleLabel(member.role)}</Badge>,
    },
    status: {
      ariaSort: sortBy === 'status' ? direction === 'asc' ? 'ascending' : 'descending' : 'none',
      key: 'status',
      header: header('status'),
      renderCell: (member) => (
        <Badge variant={member.banned ? 'destructive' : 'outline'}>
          {member.banned ? 'Désactivé' : 'Actif'}
        </Badge>
      ),
    },
    emailVerified: {
      ariaSort: sortBy === 'emailVerified'
        ? direction === 'asc' ? 'ascending' : 'descending'
        : 'none',
      key: 'emailVerified',
      header: header('emailVerified'),
      renderCell: (member) => (
        <Badge variant={member.emailVerified ? 'outline' : 'secondary'}>
          {member.emailVerified ? 'Vérifié' : 'Invitation en attente'}
        </Badge>
      ),
    },
    consumed: {
      key: 'consumed',
      // Non triable : le cumul n'est pas une colonne triable côté API.
      header: companyMemberColumnLabels.consumed,
      headerClassName: 'text-right',
      cellClassName: 'text-right tabular-nums',
      renderCell: (member) => (
        <span title={`${member.tokensConsumed.toLocaleString('fr-FR')} tokens`}>
          {formatCompactNumber(member.tokensConsumed)} tokens
        </span>
      ),
    },
    createdAt: {
      ariaSort: sortBy === 'createdAt' ? direction === 'asc' ? 'ascending' : 'descending' : 'none',
      key: 'createdAt',
      header: header('createdAt'),
      headerClassName: 'text-right',
      cellClassName: 'text-right',
      renderCell: (member) => formatDateTime(member.createdAt),
    },
  };
  return allCompanyMemberColumns
    .filter((key) => visible.has(key))
    .map((key) => columns[key]);
}
